'use client';

import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useDialogs } from '@/components/dialogs/hooks';
import { type INDBFlow, saveLocalNDBFlow } from '@/helpers/NDBFlow';
import { openNDBModals } from '@/helpers/NDBModals';
import { useNavigation } from '@/hooks/navigation';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import {
  STORAGE,
  useGetCommonStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';

export const useNDBConfirmEmailModals = () => {
  const useCommonStore = useGetCommonStore();

  const { isNDBFlow, isAfterReleaseDate } = useNDBFlow();
  const open = useModalsStore.use.open();
  const { modal } = useDialogs();
  const { navigate } = useNavigation();

  const {
    isAuthenticated,
    pendingUser,
    user,
    hasUserUsedFreespins,
    hasUserUsedBonuses,
  } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingUser: state.pendingUser,
      user: state.user,
      hasUserUsedFreespins: state.hasUserUsedFreespins,
      hasUserUsedBonuses: state.hasUserUsedBonuses,
    }))
  );

  useEffect(() => {
    if (!(isAuthenticated && user) || pendingUser) return;
    const hasUserUsedAnyBonuses =
      hasUserUsedFreespins !== false || hasUserUsedBonuses !== false;

    const ndbFlow = useLocalStorageStore
      .getState()
      .get<INDBFlow | null>(STORAGE.NDB_FLOW);

    if (ndbFlow?.isPendingOAuthNDBRedirect) {
      saveLocalNDBFlow({ isPendingOAuthNDBRedirect: null });
      const modalParam = user.confirmedAt
        ? 'ndb-email-verified'
        : 'ndb-email-confirm';

      if (user.confirmedAt && hasUserUsedAnyBonuses) return;
      navigate(`/?modal=${modalParam}`, false, true);
      return;
    }

    if (modal || !isNDBFlow || hasUserUsedAnyBonuses) return;

    openNDBModals(ndbFlow, isAfterReleaseDate, user.confirmedAt, open);
  }, [
    isAuthenticated,
    pendingUser,
    user,
    modal,
    hasUserUsedFreespins,
    hasUserUsedBonuses,
    isNDBFlow,
    isAfterReleaseDate,
    navigate,
    open,
  ]);
};
